/**
 * Deterministic, offline Chicago (notes-bibliography) bibliography linter.
 *
 * Pure string-in → findings-out, mirroring the shape of the MLA linter
 * (`src/lib/citation-check-mla.ts`). This module is intentionally SELF-CONTAINED: it does
 * NOT import or touch the citation-formatting core (`src/lib/citation.ts`,
 * `src/lib/bibliography.ts`) or any CSL fixture, so it can never regress a golden
 * snapshot. Every finding is *advisory* — a "likely issue to review", never a claim that
 * a reference is correct. The checks are deliberately high-precision (a false positive is
 * worse than a miss).
 *
 * Chicago bibliography conventions this targets: only the FIRST author is inverted
 * ("Smith, John, and Jane Doe" — subsequent authors stay in natural order), authors are
 * joined with the word "and" (not "&"), titles use Title Case (called headline style in
 * CMOS), entries end with a period, DOIs are cited as https://doi.org/… links, and access
 * dates are used only for undated online sources.
 */

export type Confidence = "high" | "medium";

export interface Finding {
  /** Stable rule id (used as React key / for tests). */
  rule: string;
  /** Short human-readable label. */
  title: string;
  /** Explanation of what looks off and why. */
  message: string;
  /** Optional copy-to-clipboard suggested fix. */
  suggestion?: string;
  confidence: Confidence;
  /** "Why" link — an authoritative Chicago guideline or one of our own tools. */
  learnMoreUrl: string;
  learnMoreLabel: string;
}

export interface EntryReport {
  /** 1-based position in the pasted list. */
  position: number;
  /** The normalized single-line entry text that was checked. */
  text: string;
  findings: Finding[];
}

export interface CheckReport {
  entries: EntryReport[];
  /** List-level findings (ordering + duplicates) that span multiple entries. */
  listFindings: Finding[];
  totalEntries: number;
  totalFindings: number;
}

// --- Module-level regexes (Biome: regex must not live inside loops) ------------

const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_BLANK_LINE_SPLIT = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
const RE_SENTENCE_BREAK = /\.\s/;
const RE_LEADING_ARTICLE = /^(?:a|an|the)\s+/i;
const RE_QUOTED_TITLE = /["“”]([^"“”]+)["“”]/;
const RE_BAD_DOI = /(dx\.doi\.org|http:\/\/(?:dx\.)?doi\.org)/i;
const RE_DX_OR_HTTP_DOI = /https?:\/\/(?:dx\.)?doi\.org\/(\S+?)[).]?$/i;
const RE_ENDS_WITH_URL = /(https?:\/\/\S+|\b10\.\d{4,}\/\S+)$/;
const RE_TERMINAL_PUNCT = /[.?!]$/;
const RE_AMPERSAND = /\s&\s|,?\s&\s/;
const RE_AMPERSAND_REPLACE = /,?\s&\s/;
const RE_FIRST_LAST = /^[A-Z][a-z]+(?:\s+[A-Z][a-z]*\.?)*\s+[A-Z][a-z]+\b/;
const RE_TWO_WORD_NAME = /^([A-Z][a-z]+)\s+([A-Z][a-z]+)$/;
const RE_INVERTED_AFTER_AND = /\band\s+([A-Z][a-z]+),\s+([A-Z][a-z]+\.?)\s*$/;
const RE_ACCESSED = /\baccessed\b/i;
const RE_ACCESSED_CLAUSE = /accessed\s+[A-Za-z]+\.?\s+\d{1,2},?\s+\d{4}/gi;
const RE_ACCESSED_WORD = /\baccessed\b[^.]*/gi;
const RE_TOKEN_SPLIT = /\s+/;
const RE_TOKEN_PUNCT = /[^a-z]/g;
const RE_NON_LETTER = /[^a-z]/g;
const RE_NON_LETTER_KEEP_CASE = /[^a-zA-Z]/g;
const RE_WORD_SPLIT = /\s+/;
const RE_PUNCT_STRIP = /[^a-z0-9]+/g;
const RE_LEADING_LOWER = /^[a-z]/;
const RE_TRAILING_COMMA = /,$/;
const RE_PIPES = /\|/g;
const RE_YEAR_ANYWHERE = /\b(\d{4})\b/;

/**
 * Words Chicago headline style leaves lowercase inside a title (articles, coordinating
 * conjunctions, and prepositions) — so a correct Chicago Title-Case title can legitimately
 * contain lowercase words like "through" or "between". The Title-Case heuristic ignores
 * these so it only judges genuine *principal* words.
 */
const CHICAGO_LOWERCASE_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "but",
  "for",
  "nor",
  "or",
  "so",
  "yet",
  "as",
  "at",
  "by",
  "in",
  "of",
  "off",
  "on",
  "per",
  "to",
  "up",
  "via",
  "with",
  "from",
  "into",
  "onto",
  "over",
  "than",
  "upon",
  "near",
  "amid",
  "atop",
  "unto",
  "till",
  "save",
  "plus",
  "among",
  "around",
  "about",
  "above",
  "after",
  "along",
  "below",
  "beside",
  "between",
  "beyond",
  "during",
  "except",
  "inside",
  "toward",
  "under",
  "until",
  "within",
  "without",
  "across",
  "against",
  "before",
  "behind",
  "beneath",
  "outside",
  "through",
  "towards",
  "throughout",
  "regarding",
  "concerning",
  "despite",
  "versus",
  "amongst",
  "besides",
]);

// Chicago guideline references reused across findings (official CMOS quick guide).
const CHICAGO_GUIDE_URL =
  "https://www.chicagomanualofstyle.org/tools_citationguide/citation-guide-1.html";

/**
 * Split pasted text into individual bibliography entries. Blank-line-separated blocks are
 * treated as one entry each (wrapped/hanging-indent lines are rejoined with a space);
 * otherwise each non-empty line is its own entry.
 */
export function splitEntries(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (RE_BLANK_LINE.test(trimmed)) {
    return trimmed
      .split(RE_BLANK_LINE_SPLIT)
      .map((block) =>
        block
          .split(RE_NEWLINE)
          .map((line) => line.trim())
          .join(" ")
          .trim()
      )
      .filter(Boolean);
  }
  return trimmed
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * The author segment is everything before the first sentence-ending period (the period
 * that closes the Chicago author element). There is no "(year)" delimiter in a Chicago
 * bibliography entry, so we rely on the first ". " boundary.
 */
function authorSegment(entry: string): string | null {
  const idx = entry.search(RE_SENTENCE_BREAK);
  if (idx <= 0) {
    return null;
  }
  return entry.slice(0, idx).trim().replace(RE_TRAILING_COMMA, "");
}

/**
 * The title segment is the quoted source title if present (article/chapter), otherwise the
 * second sentence-delimited element (a standalone book/work title).
 */
function titleSegment(entry: string): string | null {
  const quoted = RE_QUOTED_TITLE.exec(entry);
  if (quoted) {
    return quoted[1].trim();
  }
  const idx = entry.search(RE_SENTENCE_BREAK);
  if (idx < 0) {
    return null;
  }
  const rest = entry.slice(idx + 1).trim();
  const next = rest.search(RE_SENTENCE_BREAK);
  if (next < 0) {
    return rest.replace(RE_TERMINAL_PUNCT, "").trim();
  }
  return rest.slice(0, next).trim();
}

/**
 * Tokens that mark an author segment as an ORGANIZATION / corporate body rather than a
 * personal name. Corporate authors (UN/WHO/government/NGO/university-press reports) are
 * cited in Chicago *as-is* — "United Nations." is already correct and must NOT be flagged
 * as a "First Last" personal name. Because a false positive is worse than a miss, any
 * author segment containing one of these institutional or function words is treated as an
 * organization and skipped by the author-inversion rules.
 */
const ORG_INDICATOR_WORDS = new Set([
  "academy",
  "administration",
  "agency",
  "alliance",
  "american",
  "association",
  "authority",
  "board",
  "british",
  "bureau",
  "center",
  "centre",
  "coalition",
  "college",
  "commission",
  "committee",
  "company",
  "congress",
  "consortium",
  "corporation",
  "council",
  "department",
  "european",
  "federal",
  "federation",
  "for",
  "foundation",
  "fund",
  "government",
  "group",
  "institute",
  "institutes",
  "institution",
  "international",
  "laboratory",
  "league",
  "ministry",
  "national",
  "nations",
  "network",
  "office",
  "of",
  "organisation",
  "organization",
  "press",
  "program",
  "programme",
  "republic",
  "royal",
  "service",
  "services",
  "society",
  "states",
  "trust",
  "union",
  "united",
  "university",
  "world",
]);

/**
 * Heuristic: does the author segment look like an organization/corporate body rather than
 * a personal name? True when any whitespace token is an institutional or function word
 * from `ORG_INDICATOR_WORDS` (e.g. "United Nations", "World Health Organization").
 * Deliberately conservative — these words effectively never appear in a personal
 * given/surname, so this trades a rare miss for high precision.
 */
function looksOrganizational(authorSeg: string): boolean {
  return authorSeg
    .split(RE_TOKEN_SPLIT)
    .some((token) =>
      ORG_INDICATOR_WORDS.has(token.toLowerCase().replace(RE_TOKEN_PUNCT, ""))
    );
}

/** Build "Smith, John" (inverted first author) when unambiguous. */
function invertSimpleName(authorSeg: string): string | null {
  const match = RE_TWO_WORD_NAME.exec(authorSeg);
  if (!match) {
    return null;
  }
  const [, first, last] = match;
  return `${last}, ${first}`;
}

/** Rule 1 — first author looks like "First Last" instead of Chicago "Last, First". */
function checkAuthorOrder(authorSeg: string): Finding | null {
  if (authorSeg.includes(",")) {
    return null;
  }
  if (!RE_FIRST_LAST.test(authorSeg)) {
    return null;
  }
  // Corporate/organization authors ("United Nations.", "World Health Organization.") are
  // already correct in Chicago — never flag them as a misordered personal name.
  if (looksOrganizational(authorSeg)) {
    return null;
  }
  const suggestion = invertSimpleName(authorSeg);
  return {
    rule: "author-name-order",
    title: 'First author may be in "First Last" order',
    message:
      'A Chicago bibliography inverts only the FIRST author to "Last name, First name" (e.g. "Smith, John"). This entry appears to lead with a given name. Confirm the first author is inverted.',
    suggestion: suggestion ?? undefined,
    confidence: suggestion ? "high" : "medium",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: notes and bibliography",
  };
}

/** Rule 2 — a subsequent author is inverted where Chicago keeps natural order. */
function checkSubsequentAuthorOrder(authorSeg: string): Finding | null {
  const match = RE_INVERTED_AFTER_AND.exec(authorSeg);
  if (!match) {
    return null;
  }
  if (looksOrganizational(authorSeg)) {
    return null;
  }
  const [, last, first] = match;
  return {
    rule: "subsequent-author-order",
    title: "A later author may be inverted",
    message:
      'In a Chicago bibliography only the first author is inverted — additional authors stay in natural "First Last" order, e.g. "Smith, John, and Jane Doe". The final author in this entry looks inverted.',
    suggestion: `and ${first.replace(RE_TRAILING_COMMA, "")} ${last}`,
    confidence: "medium",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: notes and bibliography",
  };
}

/** Rule 3 — "&" between authors where Chicago uses the word "and". */
function checkAndConnector(authorSeg: string): Finding | null {
  if (!RE_AMPERSAND.test(authorSeg)) {
    return null;
  }
  return {
    rule: "ampersand-vs-and",
    title: 'Use "and", not "&", before the final author',
    message:
      'In a Chicago bibliography entry, join the last two authors with the word "and" — e.g. "Smith, John, and Jane Doe". The ampersand ("&") is an APA convention, not Chicago.',
    suggestion: authorSeg.replace(RE_AMPERSAND_REPLACE, ", and "),
    confidence: "high",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: notes and bibliography",
  };
}

/** Significant words = 4+ letters, used for the Title Case heuristic. */
function principalWords(title: string): string[] {
  return title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(1) // the first word is always capitalized in both title and sentence case
    .filter((w) => w.replace(RE_NON_LETTER_KEEP_CASE, "").length >= 4)
    .filter((w) => !CHICAGO_LOWERCASE_WORDS.has(w.toLowerCase()));
}

/**
 * Rule 4 — a source/article title typed in sentence case where Chicago wants headline-style
 * Title Case. High-precision: only fires when EVERY principal (4+ letter, non-function)
 * word after the first is lowercase — a real Title-Case title (even one that lowercases
 * prepositions like "through" or "of") keeps its content words capitalized and so will not
 * trip this.
 */
function checkTitleCase(entry: string): Finding | null {
  const title = titleSegment(entry);
  if (!title) {
    return null;
  }
  const words = principalWords(title);
  if (words.length < 3) {
    return null;
  }
  const allLowercase = words.every((w) => RE_LEADING_LOWER.test(w));
  if (!allLowercase) {
    return null;
  }
  return {
    rule: "title-case-smell",
    title: "Title may be in sentence case",
    message:
      'Chicago uses headline-style Title Case for titles of books and articles — capitalize the first word, the last word, and all principal words, e.g. "The Effects of Sleep on Memory". This title looks like it is in sentence case.',
    confidence: "medium",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: notes and bibliography",
  };
}

/** Rule 6 — http:// or dx.doi.org DOI instead of a clean https://doi.org link. */
function checkDoi(entry: string): Finding | null {
  if (!RE_BAD_DOI.test(entry)) {
    return null;
  }
  const dx = RE_DX_OR_HTTP_DOI.exec(entry);
  const doiTail = dx?.[1];
  return {
    rule: "doi-url-format",
    title: "DOI uses an outdated host or insecure scheme",
    message:
      "Chicago recommends citing a DOI as a clean https://doi.org/10.xxxx/yyyy link. Avoid the deprecated dx.doi.org host and the insecure http:// scheme.",
    suggestion: doiTail ? `https://doi.org/${doiTail}` : undefined,
    confidence: "high",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: DOIs and URLs",
  };
}

/** Rule 5 — entry does not end in a period (and does not end in a URL/DOI). */
function checkTerminalPeriod(entry: string): Finding | null {
  const trimmed = entry.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) || RE_TERMINAL_PUNCT.test(trimmed)) {
    return null;
  }
  return {
    rule: "terminal-period",
    title: "Entry does not end with a period",
    message:
      "Every Chicago bibliography entry ends with a period. This entry ends without terminal punctuation.",
    confidence: "high",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: notes and bibliography",
  };
}

/**
 * Rule 7 — an access date on an entry that already has a publication/revision year.
 * Chicago reserves "Accessed Month Day, Year" for online sources with NO publication or
 * revision date, so an access date next to a publication year is usually unnecessary.
 */
function checkAccessDate(entry: string): Finding | null {
  if (!RE_ACCESSED.test(entry)) {
    return null;
  }
  const withoutAccessClause = entry
    .replace(RE_ACCESSED_CLAUSE, "")
    .replace(RE_ACCESSED_WORD, "");
  if (!RE_YEAR_ANYWHERE.test(withoutAccessClause)) {
    return null;
  }
  return {
    rule: "access-date-unnecessary",
    title: "Access date may be unnecessary",
    message:
      "Chicago uses an access date only for online sources that have NO publication or revision date. This entry appears to include both a publication year and an access date — if the source is dated, drop the access date.",
    confidence: "medium",
    learnMoreUrl: CHICAGO_GUIDE_URL,
    learnMoreLabel: "Chicago: access dates",
  };
}

/** Run all per-entry rules for a single normalized entry. */
function checkEntry(entry: string): Finding[] {
  const findings: Finding[] = [];
  const authorSeg = authorSegment(entry);
  if (authorSeg) {
    const order = checkAuthorOrder(authorSeg);
    if (order) {
      findings.push(order);
    }
    const subsequent = checkSubsequentAuthorOrder(authorSeg);
    if (subsequent) {
      findings.push(subsequent);
    }
    const connector = checkAndConnector(authorSeg);
    if (connector) {
      findings.push(connector);
    }
  }
  const titleCase = checkTitleCase(entry);
  if (titleCase) {
    findings.push(titleCase);
  }
  const period = checkTerminalPeriod(entry);
  if (period) {
    findings.push(period);
  }
  const doi = checkDoi(entry);
  if (doi) {
    findings.push(doi);
  }
  const access = checkAccessDate(entry);
  if (access) {
    findings.push(access);
  }
  return findings;
}

/** First-author surname (or article-stripped title) used for ordering + duplicate keys. */
function sortKey(entry: string): string {
  const authorSeg = authorSegment(entry);
  let basis: string;
  if (authorSeg) {
    basis = authorSeg.includes(",")
      ? authorSeg.slice(0, authorSeg.indexOf(","))
      : (authorSeg.split(RE_WORD_SPLIT).pop() ?? authorSeg);
  } else {
    basis = entry.replace(RE_LEADING_ARTICLE, "");
  }
  return basis.toLowerCase().replace(RE_NON_LETTER, "");
}

/** Rule 8 — flag entries that break alphabetical (A–Z by surname) order. */
function checkOrdering(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const keys = entries.map(sortKey);
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1];
    const cur = keys[i];
    if (prev && cur && prev.localeCompare(cur) > 0) {
      findings.push({
        rule: "list-ordering",
        title: `Entry ${i + 1} may be out of alphabetical order`,
        message: `A Chicago bibliography is ordered A–Z by the first author's surname. Entry ${i + 1} sorts before entry ${i} ("${cur}" < "${prev}"). Check the ordering of these two entries.`,
        confidence: "high",
        learnMoreUrl: CHICAGO_GUIDE_URL,
        learnMoreLabel: "Chicago: bibliography order",
      });
    }
  }
  return findings;
}

/** Near-duplicate identity: surname + year (if any) + first significant title words. */
function duplicateKey(entry: string): string {
  const surname = sortKey(entry);
  const yearMatch = RE_YEAR_ANYWHERE.exec(entry);
  const year = yearMatch ? yearMatch[1] : "";
  const title = titleSegment(entry) ?? "";
  const titleWord = title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(0, 4)
    .join("")
    .toLowerCase()
    .replace(RE_PUNCT_STRIP, "");
  return `${surname}|${year}|${titleWord}`;
}

/** Rule 9 — flag near-duplicate entries (same surname + year + title start). */
function checkDuplicates(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const seen = new Map<string, number>();
  for (let i = 0; i < entries.length; i++) {
    const key = duplicateKey(entries[i]);
    if (!key || key.replace(RE_PIPES, "") === "") {
      continue;
    }
    const firstIndex = seen.get(key);
    if (firstIndex === undefined) {
      seen.set(key, i);
      continue;
    }
    findings.push({
      rule: "duplicate-entry",
      title: `Entry ${i + 1} may duplicate entry ${firstIndex + 1}`,
      message: `Entries ${firstIndex + 1} and ${i + 1} share the same author, year, and title start. A bibliography should list each source once.`,
      confidence: "high",
      learnMoreUrl: CHICAGO_GUIDE_URL,
      learnMoreLabel: "Chicago: bibliography",
    });
  }
  return findings;
}

/**
 * Run the full deterministic Chicago bibliography check over pasted text. Pure — no
 * network, no mutation, no dependency on the citation core.
 */
export function checkReferenceList(text: string): CheckReport {
  const rawEntries = splitEntries(text);
  const entries: EntryReport[] = rawEntries.map((entry, index) => ({
    position: index + 1,
    text: entry,
    findings: checkEntry(entry),
  }));
  const listFindings = [
    ...checkOrdering(rawEntries),
    ...checkDuplicates(rawEntries),
  ];
  const totalFindings =
    listFindings.length +
    entries.reduce((sum, e) => sum + e.findings.length, 0);
  return {
    entries,
    listFindings,
    totalEntries: entries.length,
    totalFindings,
  };
}
